import { useEffect, useState } from 'react';
import type { ClothingOption, StyleSample } from '../types';
import { StyleSelector } from './StyleSelector';

// Daily budget is 8 total (RATE_LIMIT_PER_DAY): the 1 initial generation +
// up to 7 retries. The server counts gen + retry in one per-IP/day counter.
export const MAX_RETRIES = 7;

interface Props {
  samples: StyleSample[];
  currentStyleId: string;
  retryCount: number;
  disabled?: boolean;
  onRetry: (newStyleId: string, clothing?: string) => void;
  /** "Upload new photos" → back to the product page to start over. */
  onUploadNew: () => void;
  /** familypet-caption-45/-50: the photo count is fixed by the original
   *  upload, so retry must NOT let the customer re-pick the person/pet combo
   *  (that's what `currentStyleId` encodes here) — only Clothing Preference. */
  lockCombo?: boolean;
  clothingOptions?: ClothingOption[];
  clothingLabel?: string;
  clothingDefaultId?: string;
}

/**
 * RetryPanel — the woo "Recreate Your Portrait" flow, two steps:
 *   1. Choice — Change style (keep image) / Upload new photos (start over).
 *   2. Choose style — the style grid + "Retry Now". For `lockCombo` modes
 *      (familypet-caption-45/-50) step 2 is a Clothing Preference picker
 *      instead — the combo itself can't change on retry.
 * Mirrors template-woo bf-retry-panel (bf-retry-choice-* + bf-style-items).
 */
export function RetryPanel({
  samples,
  currentStyleId,
  retryCount,
  disabled,
  onRetry,
  onUploadNew,
  lockCombo,
  clothingOptions = [],
  clothingLabel,
  clothingDefaultId,
}: Props) {
  const [step, setStep] = useState<'choice' | 'style'>('choice');
  const [pickedId, setPickedId] = useState<string>(currentStyleId);
  const [pickedClothing, setPickedClothing] = useState<string>(clothingDefaultId ?? clothingOptions[0]?.id ?? '');
  const remaining = Math.max(0, MAX_RETRIES - retryCount);
  const exhausted = remaining === 0;

  useEffect(() => {
    setPickedId(currentStyleId);
  }, [currentStyleId]);

  useEffect(() => {
    setPickedClothing(clothingDefaultId ?? clothingOptions[0]?.id ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clothingDefaultId]);

  if (step === 'choice') {
    return (
      <section className="flex flex-col gap-4 p-6 rounded-[18px] border border-soft-border bg-white shadow-soft">
        <div>
          <h3 className="font-bold text-xl text-warm-brown m-0">Recreate Your Portrait</h3>
          <p className="text-sm text-warm-brown/70 mt-1 mb-0">
            {lockCombo
              ? 'Do you want to change the clothing or upload new photos?'
              : 'Do you want to change the style or upload new photos?'}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {/* Change style/clothing — primary (gold) */}
          <button
            type="button"
            onClick={() => setStep('style')}
            disabled={exhausted}
            className="flex items-center gap-3 text-left rounded-[14px] bg-teal text-white p-4 shadow-soft hover:bg-teal-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <span className="rounded-[10px] flex-shrink-0" style={{ width: 40, height: 40, background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22a1 1 0 0 1 0-20 10 9 0 0 1 10 9 5 5 0 0 1-5 5h-2.25a1.75 1.75 0 0 0-1.4 2.8l.3.4a1.75 1.75 0 0 1-1.4 2.8z"/><circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/><circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/><circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/><circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/></svg>
            </span>
            <span className="flex-1 min-w-0">
              <span className="block font-bold text-sm tracking-[0.06em] uppercase">{lockCombo ? 'Change clothing' : 'Change style'}</span>
              <span className="block text-xs text-white/85 mt-0.5 uppercase tracking-[0.04em]">
                {lockCombo
                  ? 'Keep the same photos, choose Artist’s Choice or Keep Original.'
                  : 'Keep the image the same, change the drawing style.'}
              </span>
            </span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>
          </button>

          {/* Upload new photos — secondary */}
          <button
            type="button"
            onClick={onUploadNew}
            className="flex items-center gap-3 text-left rounded-[14px] p-4 border border-soft-border bg-[#7f63491a] text-warm-brown hover:bg-[#c582441a] transition-colors"
          >
            <span className="rounded-[10px] flex-shrink-0 text-warm-brown" style={{ width: 40, height: 40, background: 'rgba(127,99,73,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            </span>
            <span className="flex-1 min-w-0">
              <span className="block font-bold text-sm tracking-[0.06em] uppercase">Upload new photos</span>
              <span className="block text-xs text-warm-brown/65 mt-0.5 uppercase tracking-[0.04em]">Start again from the beginning.</span>
            </span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0 text-warm-brown/60" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>
          </button>
        </div>

        {exhausted && (
          <p className="text-xs text-warm-brown/60 text-center m-0">No retries left for this portrait.</p>
        )}
      </section>
    );
  }

  // step === 'style'
  return (
    <section className="flex flex-col gap-4 p-6 rounded-[18px] border border-soft-border bg-white shadow-soft">
      <header className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setStep('choice')}
          aria-label="Back"
          className="rounded-full bg-[#7f63491a] text-warm-brown hover:bg-[#c582441a] flex-shrink-0"
          style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
        </button>
        <span className="text-[13px] font-bold tracking-[0.12em] uppercase text-warm-brown">
          {lockCombo ? (clothingLabel ?? 'Clothing Preference') : 'Choose Style'}
        </span>
        {!exhausted && (
          <span className="ml-auto text-xs text-mute">{remaining} of {MAX_RETRIES} left</span>
        )}
      </header>

      {lockCombo ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 12 }}>
          {clothingOptions.map((opt) => {
            const selected = pickedClothing === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setPickedClothing(opt.id)}
                disabled={disabled}
                aria-pressed={selected}
                className={[
                  'relative flex flex-col items-center text-center gap-2 rounded-[15px] bg-white px-5 pt-8 pb-6 shadow-soft transition-colors',
                  selected ? 'border-2 border-teal' : 'border-2 border-soft-border hover:border-teal/50',
                ].join(' ')}
              >
                {opt.recommended && (
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-teal px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.05em] text-white">
                    Recommended
                  </span>
                )}
                {selected && (
                  <span className="absolute top-3 right-3 flex h-[22px] w-[22px] items-center justify-center rounded-full bg-teal text-xs text-white">
                    ✓
                  </span>
                )}
                <span className="text-[28px] leading-none text-teal" aria-hidden="true">{opt.icon}</span>
                <span className="text-[15px] font-semibold text-warm-brown">{opt.title}</span>
                <span className="text-xs leading-relaxed text-warm-brown/60">{opt.sub}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <StyleSelector samples={samples} selectedId={pickedId} onSelect={setPickedId} />
      )}

      <button
        type="button"
        onClick={() => onRetry(pickedId, lockCombo ? pickedClothing : undefined)}
        disabled={disabled || exhausted}
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-[10px] bg-teal text-white text-sm font-bold shadow-soft hover:bg-teal-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M1 4v6h6M23 20v-6h-6"/><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15"/></svg>
        {disabled ? 'Working…' : 'Retry Now'}
      </button>
    </section>
  );
}
